/**
 * <article-toc> — builds a table of contents from the headings inside
 * `.article__content`. Assigns ids to those headings and links to them.
 * Stays hidden (via the `hidden` attribute) unless it finds at least
 * `data-min` headings.
 */
(function () {
  if (customElements.get('article-toc')) return;

  function slugify(text) {
    return (
      text
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\s ]+/g, '-')
        // keep unicode letters/numbers/dashes, drop the rest
        .replace(/[^\p{L}\p{N}-]+/gu, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'bolum'
    );
  }

  class ArticleToc extends HTMLElement {
    connectedCallback() {
      // Defer until the article content is in the DOM.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.build(), { once: true });
      } else {
        this.build();
      }
    }

    build() {
      const content =
        document.querySelector('.article__content') ||
        document.querySelector('.article__wrapper .rte');
      if (!content) return;

      const levels = (this.dataset.levels || 'h2,h3')
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
        .join(',');
      const min = parseInt(this.dataset.min, 10) || 3;

      const headings = Array.prototype.slice
        .call(content.querySelectorAll(levels))
        .filter((h) => h.textContent.trim().length > 0);

      if (headings.length < min) {
        this.remove();
        return;
      }

      const list = this.querySelector('.article-toc__list');
      const used = Object.create(null);

      headings.forEach((heading) => {
        // Strip Word comment anchors ([MD1], [ZÇ2] etc.) from heading text
        // before generating the slug and the TOC link label.
        const cleanHeading = heading.cloneNode(true);
        cleanHeading
          .querySelectorAll('a[href^="#_msocom"], a[id^="msoanchor"]')
          .forEach((el) => el.remove());
        const cleanText = cleanHeading.textContent.trim();

        let id = heading.id;
        if (!id) {
          id = slugify(cleanText);
          let candidate = id;
          let i = 2;
          while (used[candidate] || document.getElementById(candidate)) {
            candidate = id + '-' + i++;
          }
          id = candidate;
          heading.id = id;
        }
        used[id] = true;
        heading.classList.add('article-heading-anchor');

        const li = document.createElement('li');
        li.className = 'article-toc__entry article-toc__entry--' + heading.tagName.toLowerCase();
        const a = document.createElement('a');
        a.href = '#' + id;
        a.textContent = cleanText;
        a.className = 'article-toc__link';
        li.appendChild(a);
        list.appendChild(li);
      });

      if (this.dataset.collapsible !== 'false' && window.matchMedia('(max-width: 989px)').matches) {
        this.classList.add('article-toc--collapsible');
        const title = this.querySelector('.article-toc__title');
        if (title) {
          title.setAttribute('role', 'button');
          title.setAttribute('tabindex', '0');
          title.setAttribute('aria-expanded', 'false');
          this.classList.add('is-collapsed');
          const toggle = () => {
            const collapsed = this.classList.toggle('is-collapsed');
            title.setAttribute('aria-expanded', String(!collapsed));
          };
          title.addEventListener('click', toggle);
          title.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle();
            }
          });
        }
      }

      this.hidden = false;
    }
  }

  customElements.define('article-toc', ArticleToc);
})();
